import { useMemo } from 'react'
import { errors } from '@retailer-platform/dashboard/errors'
import { useOptionalWarehouseContext } from '@retailer-platform/dashboard/utils'
import { InstacartCustomersSharedV1Locale } from '../../api'

/**
 * Locale mapping from warehouse locale codes to InstacartCustomersSharedV1Locale enum
 * Based on patterns found in domains/storefront/src/components/navigation/constants/locale-map.ts
 * All keys are uppercase - input is normalized to uppercase before lookup
 */
const LOCALE_MAP: Record<string, InstacartCustomersSharedV1Locale> = {
  // English variants - map to EN_US
  EN_AU: InstacartCustomersSharedV1Locale.EnAu,
  EN_CA: InstacartCustomersSharedV1Locale.EnCa,
  EN_US: InstacartCustomersSharedV1Locale.EnUs,

  // French variants
  FR_CA: InstacartCustomersSharedV1Locale.FrCa,
  FR_FR: InstacartCustomersSharedV1Locale.FrFr,

  // Spanish variants
  ES_US: InstacartCustomersSharedV1Locale.EsUs,

  // German variants
  DE_AT: InstacartCustomersSharedV1Locale.DeAt,

  // Austrian English
  EN_AT: InstacartCustomersSharedV1Locale.EnAt,
}

const DEFAULT_LOCALE_ENUM = InstacartCustomersSharedV1Locale.EnUs

export interface UseRetailerLocaleResult {
  /** The raw locale code string from warehouse context (e.g., 'en_US', 'fr_CA') */
  rawLocaleCode?: string
  /** The mapped InstacartCustomersSharedV1Locale enum value */
  localeCode: InstacartCustomersSharedV1Locale
  /** Whether the locale was loaded from warehouse context or using defaults */
  wasLoadedFromWarehouseContext: boolean
}

/**
 * Hook for retrieving retailer locale information with automatic mapping to InstacartCustomersSharedV1Locale enum.
 *
 * This hook provides a centralized way to access locale information across all domains, combining:
 * - Safe warehouse context access (doesn't throw if context missing)
 * - Automatic mapping to the GraphQL enum type
 * - Fallback defaults for marketplace scenarios
 *
 * @returns Object containing locale code string, mapped enum value, and loading state
 *
 * @example
 * ```tsx
 * const { localeCode, rawLocaleCode, wasLoadedFromWarehouseContext } = useRetailerLocale()
 *
 * // Use in GraphQL queries
 * const { data } = useGetRetailerProductsQuery({
 *   variables: {
 *     retailerId,
 *     localeCode, // InstacartCustomersSharedV1Locale.EnUs
 *   }
 * })
 *
 * // Access raw string if needed
 * console.log(`User locale: ${rawLocaleCode}`) // "en_US"
 * ```
 */
export const useRetailerLocale = (): UseRetailerLocaleResult => {
  const warehouseContext = useOptionalWarehouseContext()

  return useMemo(() => {
    const rawLocaleCode = warehouseContext?.warehouse?.defaultLocale?.localeCode
    const formattedLocaleCode = rawLocaleCode ? LOCALE_MAP[rawLocaleCode.toUpperCase()] : undefined

    if (rawLocaleCode && !formattedLocaleCode) {
      errors.captureMessage('Unsupported locale code from warehouse context', {
        level: 'warning',
        extra: {
          rawLocaleCode,
          defaultLocaleUsed: DEFAULT_LOCALE_ENUM,
        },
      })
    }

    if (!rawLocaleCode || !formattedLocaleCode) {
      return {
        rawLocaleCode,
        localeCode: DEFAULT_LOCALE_ENUM,
        wasLoadedFromWarehouseContext: false,
      }
    }

    return {
      rawLocaleCode,
      localeCode: formattedLocaleCode,
      wasLoadedFromWarehouseContext: true,
    }
  }, [warehouseContext?.warehouse?.defaultLocale?.localeCode])
}
